"use client";

import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["700"],
  variable: "--font-caveat",
});

/* ─────────────────────────────────────────────────────────────
   1. REAL ESTATE AGENCY: SHOWCASE YOUR PROPERTIES
   Top title with teal underline + 2 copy columns
   + Left: Desktop website property listing catalog card
   + Right: Mobile property detail card with "View property" button
   ───────────────────────────────────────────────────────────── */
export function RealEstateShowcasePropertiesSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto space-y-10">

        {/* TOP HEADER: Caveat Title + 2 Copy Columns */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-6 w-full mx-auto"
        >
          {/* Blue spark rays doodle top-left */}
          <div className="relative inline-block">
            <div className="absolute -top-7 -left-6 text-sky-400 select-none pointer-events-none">
              <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
                <path d="M 12 5 L 18 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M 2 18 L 12 22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M 25 2 L 25 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>

            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
              Showcase your{" "}
              <span className="relative inline-block">
                properties
                {/* Teal wavy stroke underline */}
                <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 180 12" fill="none">
                  <path d="M 2 6 Q 45 1, 90 6 T 178 6" stroke="#2DD4BF" strokeWidth="5" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h2>
          </div>

          {/* Two-column description grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Create property listings.</strong>{" "}
              List houses and apartments, along with detailed information about their layout, surface, number of rooms, energy rating, and more.
            </p>
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Custom searches</strong>{" "}
              let users filter by specific requirements, and be notified when a property that matches their criteria becomes available.
            </p>
          </div>
        </motion.div>

        {/* TWO MOCKUP CARDS SIDE-BY-SIDE / OVERLAPPING (Small & Precise) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center pt-2 max-w-[1100px] mx-auto">

          {/* LEFT CARD: DESKTOP WEBSITE CATALOG MOCKUP (lg:col-span-8, max-w-[580px]) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 w-full relative flex justify-center lg:justify-start"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-[580px] text-[7.5px] relative">

              {/* Top Nav Bar */}
              <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500 gap-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white text-[8px] font-bold flex-shrink-0">●</span>
                  <span className="font-semibold text-slate-700">Website</span>
                  <span>Site</span>
                  <span>eCommerce</span>
                  <span>Reporting</span>
                  <span>Configuration</span>
                </div>
                <div className="flex items-center gap-1.5 text-[7px]">
                  <span className="bg-violet-500 text-white w-4 h-4 rounded flex items-center justify-center font-bold">A</span>
                  <span>📱</span>
                  <span className="border border-slate-200 rounded px-1.5 py-0.5">New</span>
                  <span className="bg-[#7C5A76] text-white rounded px-1.5 py-0.5 font-semibold">Edit</span>
                </div>
              </div>

              {/* Header breadcrumb & Estimate button */}
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-amber-50/40 text-[7px]">
                <div className="flex items-center gap-2 text-slate-600 font-medium">
                  <span>🏠 Home</span>
                  <span className="font-bold text-slate-900">Properties</span>
                  <span>Matchmaking</span>
                </div>
                <div className="flex items-center gap-1 text-[6.5px]">
                  <span className="text-slate-500">Administrator ▾</span>
                  <button className="bg-teal-400 text-slate-900 font-semibold px-2 py-0.5 rounded-full shadow-sm">
                    Free estimate
                  </button>
                </div>
              </div>

              {/* Filter Tabs & Search row */}
              <div className="p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[7px]">
                    <span className="border-b-2 border-slate-900 font-bold text-slate-900 pb-0.5">For Sale</span>
                    <span className="text-slate-400">For Rent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-[6.5px] text-slate-400">
                      <span>Search...</span>
                      <span>🔍</span>
                    </div>
                    <span className="text-slate-500 text-[6.5px]">Sort By: <strong className="text-slate-800">Featured ▾</strong></span>
                  </div>
                </div>

                {/* Catalog Body: Left Filter Sidebar + Right Property Grid */}
                <div className="grid grid-cols-12 gap-3 pt-1">

                  {/* Left Sidebar Filters */}
                  <div className="col-span-3 space-y-1.5 border-r border-slate-100 pr-2 text-[6.5px] text-slate-500">
                    {["Region", "Bathrooms", "Type", "Rooms", "Condition", "Pool"].map((f, i) => (
                      <div key={i} className="flex justify-between items-center border-b border-slate-100 pb-0.5">
                        <span>{f}</span>
                        <span className="text-slate-300">▾</span>
                      </div>
                    ))}
                    <div className="pt-1 space-y-1">
                      <div className="font-bold text-slate-700">Price Range</div>
                      <div className="flex justify-between text-[5.5px] text-slate-400">
                        <span>1.00 €</span>
                        <span>1,200,000.00 €</span>
                      </div>
                      <input type="range" className="w-full accent-teal-500 h-1" />
                    </div>
                  </div>

                  {/* Right Property Grid (3 columns) */}
                  <div className="col-span-9 grid grid-cols-3 gap-2">

                    {/* Property 1 */}
                    <div className="border border-slate-100 rounded-lg overflow-hidden bg-white shadow-sm space-y-1 relative">
                      <div className="h-16 relative bg-slate-200 overflow-hidden">
                        <img
                          src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=400&q=80"
                          alt="Old farm to renovate"
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute top-0 left-0 bg-sky-500 text-white text-[5px] font-bold px-1 py-0.2 rotate-[-25deg] transform -translate-x-2 translate-y-1">New</span>
                      </div>
                      <div className="p-1 space-y-0.5 text-[6.5px]">
                        <div className="font-bold text-sky-600 truncate">Old farm to renovate</div>
                        <div className="font-bold text-slate-900">495,000.00 €</div>
                      </div>
                    </div>

                    {/* Property 2 */}
                    <div className="border border-slate-100 rounded-lg overflow-hidden bg-white shadow-sm space-y-1 relative">
                      <div className="h-16 relative bg-slate-200 overflow-hidden">
                        <img
                          src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&q=80"
                          alt="Amazing 5 bedrooms manor"
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute top-0 left-0 bg-lime-500 text-white text-[5px] font-bold px-1 py-0.2 rotate-[-25deg] transform -translate-x-2 translate-y-1">Pre-published</span>
                      </div>
                      <div className="p-1 space-y-0.5 text-[6.5px]">
                        <div className="font-bold text-teal-600 truncate">Amazing 5 bedrooms manor</div>
                        <div className="font-bold text-slate-900">480,000.00 €</div>
                      </div>
                    </div>

                    {/* Property 3 */}
                    <div className="border border-slate-100 rounded-lg overflow-hidden bg-white shadow-sm space-y-1 relative">
                      <div className="h-16 relative bg-slate-200 overflow-hidden">
                        <img
                          src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=400&q=80"
                          alt="Charming renovated farm in Incourt"
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute top-0 left-0 bg-sky-500 text-white text-[5px] font-bold px-1 py-0.2 rotate-[-25deg] transform -translate-x-2 translate-y-1">New</span>
                      </div>
                      <div className="p-1 space-y-0.5 text-[6.5px]">
                        <div className="font-bold text-teal-600 truncate">Charming renovated farm in Incourt</div>
                        <div className="font-bold text-slate-900">460,000.00 €</div>
                      </div>
                    </div>

                  </div>

                </div>
              </div>

              {/* Blue Click Pointer Icon Doodle at bottom right corner */}
              <div className="absolute bottom-2 right-2 flex items-center gap-1 select-none pointer-events-none z-20">
                <svg className="w-8 h-8 text-sky-400" viewBox="0 0 32 32" fill="none">
                  <path d="M 8 20 L 14 26 L 18 18 L 26 18 Z" fill="#0EA5E9" stroke="#0284C7" strokeWidth="1.5" />
                  <line x1="2" y1="26" x2="6" y2="24" stroke="#38BDF8" strokeWidth="2" strokeLinecap="round" />
                  <line x1="4" y1="30" x2="8" y2="27" stroke="#38BDF8" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

            </div>
          </motion.div>

          {/* RIGHT CARD: MOBILE PROPERTY DETAIL CARD (lg:col-span-4, max-w-[250px]) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-4 w-full flex justify-center lg:justify-start"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-[250px] p-3 space-y-3">

              {/* Mobile top bar */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-1.5 text-[7px] text-slate-500">
                <span>☰</span>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                  <span className="w-3.5 h-3.5 rounded bg-purple-600 text-white font-bold flex items-center justify-center text-[6px]">A</span>
                </div>
              </div>

              {/* Agency Logo & Title Header */}
              <div className="text-center space-y-1 pt-1">
                <div className="flex justify-center items-center gap-1 text-[8px] font-bold text-teal-600">
                  <svg className="w-4 h-4 text-teal-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3L2 12h3v8h14v-8h3L12 3z" />
                  </svg>
                  <span>Your Real Estate Agency</span>
                </div>

                <h3 className="text-xs font-bold text-slate-900 leading-tight pt-1">
                  We may have found<br />your dream house
                </h3>
              </div>

              {/* Featured Property Image Card */}
              <div className="rounded-xl overflow-hidden border border-slate-100 shadow-md bg-slate-50 space-y-2">
                <div className="h-32 w-full overflow-hidden bg-slate-200">
                  <img
                    src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&q=80"
                    alt="Amazing 5 bedrooms manor"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-2 text-center space-y-2">
                  <div className="font-bold text-[8px] text-slate-900">
                    Amazing 5 bedrooms manor
                  </div>
                  <button className="bg-teal-400 hover:bg-teal-500 text-slate-900 font-bold text-[7px] px-3 py-1 rounded-full shadow-sm">
                    View property
                  </button>
                </div>
              </div>

            </div>
          </motion.div>

        </div>

        {/* Testimonial Callout Pill */}
        <div className="flex justify-end pr-4 gap-2 pt-4">
          <img
            src="/odoo/unsplash/avatars/u3.jpg"
            alt="Avatar"
            className="w-14 h-14 rounded-full object-cover border border-white/30"
          />
          <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center flex items-center">
            <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
              Build your website with Odoo's AI-based website builder!
            </i>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. REAL ESTATE AGENCY: NOURISH RELATIONSHIPS
   Top handwritten Caveat title with red underline + 3 copy columns
   + Overlapping CRM Kanban card + Appointments Table card
   + Red Calendar Doodle Accent
   ───────────────────────────────────────────────────────────── */
export function RealEstateNourishRelationshipsSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto space-y-10">

        {/* TOP HEADER: Caveat title + 3 Copy Columns */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-6 w-full mx-auto"
        >
          {/* Caveat title with red underline under "Nourish" */}
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight text-left`}>
            <span className="relative inline-block">
              Nourish
              <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 140 12" fill="none">
                <path d="M 2 6 Q 35 1, 70 6 T 138 6" stroke="#F87171" strokeWidth="5" strokeLinecap="round" fill="none" />
              </svg>
            </span>{" "}
            relationships
          </h2>

          {/* Three-column description grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Centralize leads.</strong>{" "}
              Odoo's CRM allows you to capture and nurture every lead, whether it comes from your website, email, WhatsApp, or else.
            </p>
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Schedule appointments.</strong>{" "}
              Clients can book directly from the website, and your calendar is synchronized so you never double book.
            </p>
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Keep track of activities</strong>{" "}
              such as uploading and signing documents, meetings, and calls.
            </p>
          </div>
        </motion.div>

        {/* MOCKUP CONTAINER WITH OVERLAPPING CARDS + CALENDAR DOODLE */}
        <div className="relative pt-4 max-w-[1100px] mx-auto flex flex-col items-center">

          {/* Red Calendar Icon Doodle Accent (floating right) */}
          <div className="absolute top-2 right-4 sm:right-12 z-20 pointer-events-none select-none">
            <svg className="w-14 h-14 text-rose-500" viewBox="0 0 60 60" fill="none">
              <rect x="10" y="14" width="40" height="36" rx="8" fill="#FFE4E6" stroke="#F43F5E" strokeWidth="3" />
              <path d="M 10 24 L 50 24" stroke="#F43F5E" strokeWidth="3" />
              <circle cx="20" cy="10" r="3" fill="#F43F5E" />
              <circle cx="40" cy="10" r="3" fill="#F43F5E" />
              <circle cx="20" cy="32" r="2.5" fill="#F43F5E" />
              <circle cx="30" cy="32" r="2.5" fill="#F43F5E" />
              <circle cx="40" cy="32" r="2.5" fill="#F43F5E" />
              <circle cx="20" cy="40" r="2.5" fill="#F43F5E" />
              <circle cx="30" cy="40" r="2.5" fill="#F43F5E" />
            </svg>
          </div>

          {/* BACKGROUND CARD: ODOO CRM OPPORTUNITIES KANBAN (Small & Precise max-w-[620px]) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full max-w-[620px] bg-white rounded-2xl shadow-xl border border-slate-200 text-slate-800 font-sans overflow-hidden text-[7.5px]"
          >
            {/* Top Nav Bar */}
            <div className="flex items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500">
              <div className="flex items-center gap-1.5">
                <span>☰</span>
                <span className="font-semibold text-slate-700">CRM</span>
              </div>
              <div className="flex items-center gap-1 text-[7px] text-slate-400">
                <span>💬</span><span>⏱</span><span>✕</span>
                <span className="font-semibold text-slate-700 ml-1">Horizon Homes</span>
                <span className="inline-flex w-4 h-4 rounded-full bg-violet-600 items-center justify-center text-white text-[7px] font-bold">A</span>
              </div>
            </div>

            {/* Sub-nav: New + Generate Leads + Opportunities */}
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[7.5px]">
              <div className="flex items-center gap-1.5">
                <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                <span className="border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[7px]">Generate Leads</span>
                <span className="font-semibold text-slate-700">Opportunities ⚙️</span>
              </div>
              <div className="flex items-center gap-1 text-[6.5px] text-slate-400">
                <div className="bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                  🔍 Search...
                </div>
                <button className="border border-[#7C5A76] bg-[#f5f0f5] text-[#7C5A76] rounded px-1 font-bold">▣</button>
                <button className="border border-slate-200 rounded px-1">☰</button>
                <button className="border border-slate-200 rounded px-1">📅</button>
              </div>
            </div>

            {/* CRM 4-column Grid */}
            <div className="grid grid-cols-4 divide-x divide-slate-100 bg-slate-50/20 p-2 gap-1.5 text-[7px]">
              {/* Column 1: New */}
              <div className="space-y-1">
                <div className="flex justify-between font-bold text-slate-700 pb-0.5 border-b border-slate-200">
                  <span>New +</span>
                  <span className="text-slate-400 font-normal">645k</span>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">Would Love to visit this house asap!</div>
                  <div className="text-[6px] text-slate-400">Stunning cottage with garden<br />J. Helios</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ 📞</div>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">Is the furniture sold with the house?</div>
                  <div className="text-[6px] text-slate-400">Appartment in Waterloo<br />Geraldine Gallant</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ ✉️</div>
                </div>
              </div>

              {/* Column 2: Visit */}
              <div className="space-y-1">
                <div className="flex justify-between font-bold text-slate-700 pb-0.5 border-b border-slate-200">
                  <span>Visit +</span>
                  <span className="text-slate-400 font-normal">215k</span>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">Would love to visit this house asap!</div>
                  <div className="text-[6px] text-slate-400">Stunning cottage with garden<br />60,000.00 €<br />Mark Moriset</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ 👥</div>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">I love this manor</div>
                  <div className="text-[6px] text-slate-400">Amazing 5 bedrooms manor<br />100,000.00 €</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ 👥</div>
                </div>
              </div>

              {/* Column 3: Qualified */}
              <div className="space-y-1">
                <div className="flex justify-between font-bold text-slate-700 pb-0.5 border-b border-slate-200">
                  <span>Qualified +</span>
                  <span className="text-slate-400 font-normal">100k</span>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">This is a new opportunity coming from immoweb</div>
                  <div className="text-[6px] text-slate-400">Art Deco Apartment<br />55,000.00 €</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ 👥</div>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">I love this manor</div>
                  <div className="text-[6px] text-slate-400">Amazing 5 bedrooms manor<br />45,445.00 €</div>
                  <span className="bg-purple-100 text-purple-700 text-[5.5px] px-1 rounded font-bold">VIP Customer</span>
                </div>
              </div>

              {/* Column 4: Offer */}
              <div className="space-y-1">
                <div className="flex justify-between font-bold text-slate-700 pb-0.5 border-b border-slate-200">
                  <span>Offer +</span>
                  <span className="text-slate-400 font-normal">—</span>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">Matchmaking</div>
                  <div className="text-[6px] text-slate-400">Sarah's house<br />Lisa Anderson</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ 📝</div>
                </div>
                <div className="bg-white border border-slate-100 rounded p-1.5 shadow-sm space-y-0.5">
                  <div className="font-bold text-slate-900 leading-tight">This is a new opportunity Immoweb</div>
                  <div className="text-[6px] text-slate-400">Ardennes Manor</div>
                  <div className="text-amber-400 text-[6.5px]">★★☆ 📥</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FOREGROUND CARD: APPOINTMENTS TABLE CARD (Offset bottom-right, max-w-[540px]) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="w-full max-w-[540px] bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden text-[7.5px] -mt-12 ml-auto z-10"
          >
            {/* Appointments Action Bar */}
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 bg-white text-[7.5px]">
              <div className="flex items-center gap-1.5">
                <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                <span className="border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[7px]">Share</span>
                <span className="font-semibold text-slate-800">Appointments</span>
              </div>
              <div className="flex items-center gap-2 text-[6.5px] text-slate-400">
                <div className="bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                  🔍 Search...
                </div>
                <span>1-9 / 9  ❮ ❯</span>
                <button className="border border-[#7C5A76] bg-[#f5f0f5] text-[#7C5A76] rounded px-1 font-bold">▣</button>
                <button className="border border-slate-200 rounded px-1">≡</button>
              </div>
            </div>

            {/* Appointments Table */}
            <div className="divide-y divide-slate-100 text-[7px]">
              {[
                { property: "Art Deco Apartment", duration: "1 hour Duration", upcoming: "1 Meetings Upcoming", total: "5 Meetings Total" },
                { property: "Canal House", duration: "1 hour Duration", upcoming: "0 Meetings Upcoming", total: "2 Meetings Total" },
                { property: "Charming renovated farm", duration: "1 hour Duration", upcoming: "4 Meetings Upcoming", total: "4 Meetings Total" },
                { property: "Sarah's house", duration: "1 hour Duration", upcoming: "0 Meetings Upcoming", total: "4 Meetings Total" },
              ].map((row, i) => (
                <div key={i} className="grid grid-cols-12 px-3 py-2 items-center hover:bg-slate-50">
                  <div className="col-span-4 font-bold text-slate-900 truncate">{row.property}</div>
                  <div className="col-span-2 text-slate-400 text-[6.5px]">{row.duration}</div>
                  <div className="col-span-1 flex -space-x-1">
                    <span className="w-3.5 h-3.5 rounded-full bg-slate-300 border border-white block" />
                    <span className="w-3.5 h-3.5 rounded-full bg-rose-400 border border-white block" />
                  </div>
                  <div className="col-span-2 text-slate-600 font-medium text-[6.5px]">{row.upcoming}</div>
                  <div className="col-span-2 text-slate-500 text-[6.5px]">{row.total}</div>
                  <div className="col-span-1 text-right text-slate-500 font-semibold text-[6.5px]">⚙ Action</div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. REAL ESTATE AGENCY: MANAGE DOCUMENTS, COLLECT SIGNATURES
   Left: Pencil doodle + Odoo Sign document builder card
   Right: Caveat title with yellow underlines + 2 copy blocks
   ───────────────────────────────────────────────────────────── */
export function RealEstateManageDocumentsSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">


          {/* LEFT: PENCIL DOODLE + ODOO SIGN CARD (max-w-[560px]) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative w-full flex justify-center lg:justify-start"
          >
            {/* Orange Pencil Doodle Accent top-right of card */}
            <div className="absolute -top-7 right-8 sm:right-16 z-20 pointer-events-none select-none">
              <svg className="w-12 h-12 text-amber-500 transform -rotate-45" viewBox="0 0 48 48" fill="none">
                <rect x="18" y="6" width="12" height="30" rx="2" fill="#F59E0B" stroke="#D97706" strokeWidth="2" />
                <polygon points="18,36 30,36 24,44" fill="#374151" />
                <rect x="18" y="6" width="12" height="6" fill="#F43F5E" />
              </svg>
            </div>

            {/* Odoo Sign Window Card */}
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-[560px] text-[7.5px]">

              {/* Top Nav Bar */}
              <div className="flex items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[#7C5A76] text-[8px]">✍️ Sign</span>
                  <span>Documents</span>
                  <span>Templates</span>
                  <span>Reports</span>
                  <span>Configuration</span>
                </div>
                <div className="flex items-center gap-1 text-[7px] text-slate-400">
                  <span>💬</span><span>⏱</span><span>✕</span>
                  <span className="font-semibold text-slate-700 ml-1">Horizon Homes</span>
                  <span className="inline-flex w-4 h-4 rounded-full bg-violet-600 items-center justify-center text-white text-[7px] font-bold">A</span>
                </div>
              </div>

              {/* Action Bar & Breadcrumb */}
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[7px]">
                <div className="flex items-center gap-1.5">
                  <button className="bg-[#7C5A76] text-white px-2 py-0.5 rounded font-semibold text-[7px]">Send</button>
                  <button className="bg-purple-950 text-white px-2 py-0.5 rounded font-semibold text-[7px]">Sign Now</button>
                  <span className="text-slate-400">Templates / Template "OfferTemplate.pdf" ⚙️</span>
                </div>
                <div className="text-slate-400 text-[6.5px]">Tags: ▾</div>
              </div>

              {/* Editor Toolbar */}
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1 bg-slate-50 text-[6.5px] text-slate-500">
                <span className="font-medium text-slate-700">OfferTemplate.pdf</span>
                <div className="flex items-center gap-2">
                  <span>- + 80% ▾</span>
                  <span>🔍 ∧ ∨</span>
                  <span>1 of 1 🖨</span>
                </div>
              </div>

              {/* Main Workspace: Left Tool Palette + Right Document Canvas */}
              <div className="grid grid-cols-12 gap-0 border-t border-slate-100">

                {/* Left Tool Palette */}
                <div className="col-span-4 bg-slate-50 p-2 border-r border-slate-200 space-y-2 text-[6.5px]">
                  <div className="flex justify-between items-center font-bold text-slate-700">
                    <span>Signer 1</span>
                    <span className="bg-purple-100 text-purple-700 px-1 rounded text-[5.5px]">10 !</span>
                  </div>

                  <div className="grid grid-cols-2 gap-1 text-[6px]">
                    <button className="bg-white border border-purple-200 rounded p-1 text-purple-900 font-semibold text-left">✍ Signature</button>
                    <button className="bg-white border border-purple-200 rounded p-1 text-purple-900 font-semibold text-left">✍ Initials</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">👤 Name</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">✉️ Email</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">📞 Phone</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">🏢 Company</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">A Text</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">≡ Multiline</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">☑ Checkbox</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">🔘 Radio</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">▾ Selection</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">📅 Date</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">S Strikethrough</button>
                    <button className="bg-white border border-slate-200 rounded p-1 text-slate-700 text-left">A Property</button>
                  </div>

                  <div className="flex justify-between items-center font-bold text-slate-700 pt-1 border-t border-slate-200">
                    <span>Signer 2</span>
                    <span className="bg-emerald-100 text-emerald-700 px-1 rounded text-[5.5px]">8 !</span>
                  </div>

                  <button className="w-full bg-slate-200 text-slate-700 font-bold py-1 rounded text-[6.5px]">
                    + Add signer
                  </button>
                </div>

                {/* Right Document Canvas */}
                <div className="col-span-8 bg-slate-200/60 p-3 space-y-3 text-[7px]">
                  <div className="bg-white rounded p-3 shadow-md space-y-3 border border-slate-300">

                    {/* Offer Details */}
                    <div className="space-y-1">
                      <div className="font-bold text-slate-900 text-[8px]">Offer Details</div>
                      <div className="bg-purple-50 border border-purple-200 rounded p-1 text-purple-900 font-semibold flex justify-between">
                        <span>Property</span>
                      </div>
                    </div>

                    <div className="space-y-0.5 text-[6.5px]">
                      <div className="text-slate-500 font-bold">Offered Price:</div>
                      <div className="bg-purple-50 border border-purple-200 rounded p-0.5 text-purple-900 font-semibold w-24">
                        EUR Offer
                      </div>
                    </div>

                    <div className="space-y-1 text-[6.5px]">
                      <div className="text-slate-500 font-bold">Conditions (if any):</div>
                      <div className="space-y-0.5 text-slate-700">
                        <div className="flex items-center gap-1">
                          <span className="text-purple-600 font-bold">☑</span>
                          <span>Subject to mortgage approval</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-purple-600 font-bold">☑</span>
                          <span>Subject to property inspection</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-purple-600 font-bold">☑</span>
                          <span>Other: <span className="bg-purple-50 border border-purple-200 px-1 rounded text-purple-900">Text</span></span>
                        </div>
                      </div>
                    </div>

                    {/* Validity */}
                    <div className="space-y-1 pt-1 border-t border-slate-100 text-[6px]">
                      <div className="font-bold text-slate-900 text-[7px]">Validity of the Offer</div>
                      <p className="text-slate-500 leading-tight">
                        This offer is valid until <span className="bg-purple-50 border border-purple-200 px-1 rounded text-purple-900 font-semibold">DD-MM-YYYY</span> (date and time). If not accepted in writing by that time, it shall be considered null and void.
                      </p>
                    </div>

                    {/* Buyer's Signature */}
                    <div className="space-y-1 pt-1 border-t border-slate-100 text-[6.5px]">
                      <div className="font-bold text-slate-900 text-[7.5px]">Buyer's Signature</div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div>Name: <span className="bg-purple-50 border border-purple-200 px-1 rounded text-purple-900 font-semibold">Name</span></div>
                          <div>Date: <span className="bg-purple-50 border border-purple-200 px-1 rounded text-purple-900 font-semibold">DD-MM-YYYY</span></div>
                        </div>
                        {/* Signature Box */}
                        <div className="bg-purple-50 border border-purple-300 rounded px-3 py-1.5 text-[#7C5A76] font-semibold flex items-center justify-center shadow-inner">
                          <span className={`${caveat.className} text-sm font-bold italic text-purple-800`}>Signer 1</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>

            </div>
          </motion.div>

          {/* RIGHT: TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 space-y-5 text-left"
          >
            {/* Caveat title with yellow underlines under "documents," and "signatures" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
              Manage{" "}
              <span className="relative inline-block">
                documents,
                {/* Yellow highlighter underline */}
                <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 160 12" fill="none">
                  <path d="M 2 6 Q 40 1, 80 6 T 158 6" stroke="#FBBF24" strokeWidth="6" strokeLinecap="round" fill="none" />
                </svg>
              </span><br />
              collect{" "}
              <span className="relative inline-block">
                signatures
                {/* Yellow highlighter underline */}
                <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 160 12" fill="none">
                  <path d="M 2 6 Q 40 1, 80 6 T 158 6" stroke="#FBBF24" strokeWidth="6" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h2>

            <div className="space-y-4">

              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">CodeQlik's document workspace</strong>{" "}
                supports various file formats and allows you to organize all the necessary files for closing a sale, including property plans, contracts, and legal documents.
              </p>
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Collect signatures in seconds.</strong>{" "}
                Your clients can access and review documents on any device and sign them electronically with just a few clicks.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. REAL ESTATE AGENCY: INTEGRATED FINANCE MANAGEMENT
   Left: Caveat title with teal highlight on "finance" + 2 copy blocks
   Right: PDF Invoice card (INV/2025/00022) + Teal Invoice Doodle Accent
   ───────────────────────────────────────────────────────────── */
export function RealEstateIntegratedFinanceSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT: TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-6 text-left"
          >
            {/* Caveat Handwritten Title with Teal highlight on "finance" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
              Integrated{" "}
              <span className="bg-teal-400 text-slate-900 px-3 py-0.5 rounded-xl inline-block mx-1 transform -rotate-1 shadow-md">
                finance
              </span><br />
              management
            </h2>

            <div className="space-y-4">

              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Enjoy the flexible invoicing:</strong>{" "}
                Invoice clients in one click, using their preferred method. Choose from email, snail mail, e-invoicing, or a QR code.
              </p>
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Set up a commission plan</strong>{" "}
                to reward salespersons and property sellers. Odoo calculates the commission automatically as soon as the deal is closed!
              </p>
            </div>
          </motion.div>

          {/* RIGHT: PDF INVOICE CARD + TEAL DOODLE ACCENT (max-w-[480px]) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 relative w-full flex justify-center lg:justify-end"
          >
            {/* Teal Invoice Badge Icon Doodle (top-right floating) */}
            <div className="absolute -top-6 right-2 sm:right-6 z-20 pointer-events-none select-none">
              <svg className="w-14 h-14 text-teal-400 transform rotate-12" viewBox="0 0 52 52" fill="none">
                <rect x="8" y="6" width="32" height="40" rx="6" fill="#0D9488" stroke="#14B8A6" strokeWidth="2" />
                <path d="M 16 16 L 32 16" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M 16 22 L 28 22" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <path d="M 16 28 L 32 28" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <circle cx="34" cy="34" r="8" fill="#2DD4BF" />
                <text x="34" y="37" textAnchor="middle" fill="#0F172A" fontSize="10" fontWeight="bold">$</text>
              </svg>
            </div>

            {/* Odoo PDF Invoice Document Card */}
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-[380px] p-6 space-y-4 text-[7.5px]">

              {/* Invoice Header: Company logo & Address */}
              <div className="flex justify-between items-start border-b border-slate-100 pb-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-[#1A73E8] font-bold text-xs">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12l9-9 9 9M5 10v10h14V10" />
                    </svg>
                    <span>HORIZON HOMES</span>
                  </div>
                  <div className="text-[6px] text-slate-400 italic">When your dreams become reality</div>
                </div>
                <div className="text-right text-[6.5px] text-slate-500 leading-tight">
                  <strong className="text-slate-700">Horizon Homes</strong><br />
                  Rue des Bourlottes 9<br />
                  1367 Ramillies<br />
                  Belgium
                </div>
              </div>

              {/* Billed To & Document Title */}
              <div className="space-y-1">
                <div className="text-slate-500 text-[6.5px]">Sarah Malchour</div>
                <div className="text-lg font-bold text-[#1A73E8]">Invoice INV/2025/00022</div>
              </div>

              {/* Invoice Meta Grid */}
              <div className="grid grid-cols-3 bg-slate-50 rounded-lg p-2 text-[6.5px]">
                <div>
                  <span className="text-slate-400 block">Invoice Date</span>
                  <strong className="text-slate-800">07/03/2025</strong>
                </div>
                <div>
                  <span className="text-slate-400 block">Due Date</span>
                  <strong className="text-slate-800">07/07/2025</strong>
                </div>
                <div>
                  <span className="text-slate-400 block">Source</span>
                  <strong className="text-slate-800">S00022</strong>
                </div>
              </div>

              {/* Invoice Items Table */}
              <div className="border border-slate-100 rounded-lg overflow-hidden text-[7px]">
                <div className="grid grid-cols-12 bg-slate-50 px-2 py-1 font-bold text-slate-500 border-b border-slate-100 text-[6.5px]">
                  <span className="col-span-5">Description</span>
                  <span className="col-span-2 text-right">Quantity</span>
                  <span className="col-span-2 text-right">Unit Price</span>
                  <span className="col-span-1 text-center">Taxes</span>
                  <span className="col-span-2 text-right">Amount</span>
                </div>

                <div className="divide-y divide-slate-50 text-[6.5px]">
                  <div className="grid grid-cols-12 px-2 py-1 items-center">
                    <span className="col-span-5 font-medium text-slate-800">Stunning cottage with garden</span>
                    <span className="col-span-2 text-right text-slate-600">0.02</span>
                    <span className="col-span-2 text-right text-slate-600">450,000.00</span>
                    <span className="col-span-1 text-center text-slate-300">—</span>
                    <span className="col-span-2 text-right font-bold text-slate-800">9,000.00 €</span>
                  </div>

                  <div className="grid grid-cols-12 px-2 py-1 items-center">
                    <span className="col-span-5 font-medium text-slate-800">Service Package</span>
                    <span className="col-span-2 text-right text-slate-600">1.00</span>
                    <span className="col-span-2 text-right text-slate-600">2,500.00</span>
                    <span className="col-span-1 text-center text-slate-600">21%</span>
                    <span className="col-span-2 text-right font-bold text-slate-800">2,500.00 €</span>
                  </div>

                  <div className="grid grid-cols-12 px-2 py-1 items-center">
                    <span className="col-span-5 font-medium text-slate-800">Photographer package</span>
                    <span className="col-span-2 text-right text-slate-600">1.00</span>
                    <span className="col-span-2 text-right text-slate-600">350.00</span>
                    <span className="col-span-1 text-center text-slate-600">21%</span>
                    <span className="col-span-2 text-right font-bold text-slate-800">350.00 €</span>
                  </div>
                </div>
              </div>

              {/* Totals Summary */}
              <div className="space-y-1 text-right text-[6.5px] pt-1">
                <div className="flex justify-between text-slate-500">
                  <span>Untaxed Amount:</span>
                  <span className="font-semibold text-slate-800">11,850.00 €</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>VAT 21% on 2,850.00 €:</span>
                  <span className="font-semibold text-slate-800">598.50 €</span>
                </div>
                <div className="flex justify-between text-[#1A73E8] font-bold text-[8.5px] border-t border-slate-200 pt-1">
                  <span>Total:</span>
                  <span>12,448.50 €</span>
                </div>
              </div>

              {/* Footer info */}
              <div className="border-t border-slate-100 pt-2 flex flex-wrap justify-between text-[5.5px] text-slate-400">
                <span>Payment Communication: <strong className="text-slate-600">+++000/0000/02525+++</strong></span>
                <span className="text-[#1A73E8]">Terms & Conditions</span>
                <span>Page 1 / 1</span>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}



